import { useLayoutEffect, useState } from 'react'

import Box from '@mui/material/Box'
import { useTheme } from '@mui/material/styles'
import useMediaQuery from '@mui/material/useMediaQuery'

import { Navigation } from '../../nav'
import { Menu, SIDEBAR_WIDTH } from '../../nav/components/Menu'
import { Outlet } from '../../router/components/Outlet'
import { SidebarLayoutContext } from '../context/SidebarLayoutContext'
import { useLayoutStore } from '../hooks/useLayoutStore'

import { SidebarLayoutPanel } from './SidebarLayoutPanel'

export { SidebarLayout }

const SidebarLayout = () => {
  const theme = useTheme()
  const { xl } = theme.breakpoints.values

  const {
    sidebarOpen: settledSidebarOpen,
    storeSidebarOpen,
    toggleSidebar,
    activeDrawer,
    removeDrawer,
  } = useLayoutStore()
  const [breadcrumbsSlot, setBreadcrumbsSlot] = useState<HTMLElement | null>(
    null,
  )
  const [drawerSlot, setDrawerSlot] = useState<HTMLElement | null>(null)

  const sidebarVariant = useMediaQuery(theme.breakpoints.up(xl + SIDEBAR_WIDTH))
    ? 'permanent'
    : 'temporary'
  const sidebarOpen = settledSidebarOpen ?? sidebarVariant === 'permanent'
  const sidebarWidth =
    sidebarVariant === 'permanent' && sidebarOpen ? SIDEBAR_WIDTH : 0
  const drawerWidth = activeDrawer?.width ?? 0
  const drawerVariant = useMediaQuery(
    theme.breakpoints.up(xl + sidebarWidth + drawerWidth),
  )
    ? 'permanent'
    : 'temporary'
  const closeSidebar = () => storeSidebarOpen(false)

  useLayoutEffect(() => {
    if (settledSidebarOpen === null) {
      storeSidebarOpen(sidebarOpen)
    }
  }, [settledSidebarOpen, sidebarOpen, storeSidebarOpen])

  return (
    <SidebarLayoutContext.Provider value={{ breadcrumbsSlot, drawerSlot }}>
      <Navigation
        onMenuClick={toggleSidebar}
        breadcrumbsSlotRef={setBreadcrumbsSlot}
      />

      <Box sx={{ display: 'flex', flexGrow: 1 }}>
        <SidebarLayoutPanel
          component="nav"
          data-testid="sidebar"
          variant={sidebarVariant}
          anchor="left"
          width={SIDEBAR_WIDTH}
          open={sidebarOpen}
          onClose={closeSidebar}
        >
          {sidebarVariant === 'temporary' ? (
            <Menu onItemClick={closeSidebar} showLogo />
          ) : (
            <Menu />
          )}
        </SidebarLayoutPanel>

        <Box
          component="main"
          sx={{
            display: 'flex',
            flexDirection: 'column',
            flexGrow: 1,
            minWidth: 0,
          }}
        >
          <Outlet />
        </Box>

        <SidebarLayoutPanel
          component="aside"
          data-testid="drawer-panel"
          variant={drawerVariant}
          anchor="right"
          width={drawerWidth}
          open={activeDrawer !== null && !activeDrawer.closing}
          onClose={() => activeDrawer?.onClose()}
          onExited={() => activeDrawer && removeDrawer(activeDrawer.id)}
        >
          <Box
            ref={setDrawerSlot}
            sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}
          />
        </SidebarLayoutPanel>
      </Box>
    </SidebarLayoutContext.Provider>
  )
}
